import { createPortal } from 'react-dom';
import { Flex, Spin, Typography } from 'antd';
import styled, { keyframes } from 'styled-components';

const { Text } = Typography;

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -8px);
	}
	to {
		opacity: 1;
		transform: translate(-50%, 0);
	}
`;

const Positioner = styled.div`
	position: fixed;
	top: 84px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	pointer-events: none;

	@media (prefers-reduced-motion: no-preference) {
		animation: ${fadeIn} 0.2s ease-out;
	}
`;

// Only the shape/surface (pill, theme-colored background/border, elevation)
// stays custom CSS — antd has no component for a toast pinned at a fixed
// position over the map. Layout, the spinner, and the text below all use
// antd primitives (Flex, Spin, Typography.Text) instead.
const Pill = styled.div`
	padding: 10px 18px;
	border-radius: 999px;
	background-color: ${({ theme }) => theme.colors.modal};
	border: 1px solid ${({ theme }) => theme.colors.border};
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
	white-space: nowrap;
`;

/**
 * A non-blocking loading indicator anchored top-center over the map.
 * Rendered via a portal so it floats above the map regardless of mount point.
 */
const MapLoadingIndicator = ({ message = 'Loading', hint }) => {
	return createPortal(
		<Positioner role="status" aria-live="polite">
			<Pill>
				<Flex align="center" gap={12}>
					<Spin size="small" />
					<Text>
						{message}
						{hint && <Text type="secondary"> &middot; {hint}</Text>}
					</Text>
				</Flex>
			</Pill>
		</Positioner>,
		document.body
	);
};

export default MapLoadingIndicator;
